/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import {translate} from '../locale/locale';
import {Results} from '../services/GroupProperties';
import {
  UserBulkEditorFormFields
} from '../types/editors';
import {
  AccountLink,
  CTCEntityResponseWrapper,
  Entity,
  User
} from '../types/entities';
import {
  BulkOperationResults,
  ExtendedResults
} from '../types/services';
import {
  EntityTemplate,
  TemplateColumn,
  TemplateValidation
} from '../types/templates';
import {Mapped} from '../utils/ObjectUtil';
import {
  createValidation,
  formatBulkErrors,
  formatErrors,
  interpolateValidation,
  statusColumn
} from './TemplateHelpers';

// defines the type definitions for user template options ...
export type UserTemplateOptions = {
  groupName?: string;
  uom?: Results;
};

/**
 * Returns the template validation with error and formulae for
 * UOM column
 *
 * @param validation the validation rules for UOM column
 * @param options the pre-defined options for UOM column
 * @returns the template validation with error and formulae for
 * UOM column
 */
const UOMColumn = ({validation, options}): TemplateValidation => {
  const userUOM       = options?.uom?.toString() ?? '';
  validation.error    = interpolateValidation(validation, userUOM);
  validation.formulae = [`"${userUOM}"`];
  return validation;
};

/**
 * Provides a utility function which manages the dynamic generation of an XLSX
 * spreadsheet specific to User imports for download by the User.
 *
 * User Template column definitions are defined as localized entries within
 * the admin-Users.json localization documents for each respective locale.
 * Entries take the following form:
 *
  * ```typescript
 *
 * import {usersTemplate} from '@ion/core/src/xlsx/UserTemplate';
 *
 * const templates = {
 *   [Entity.user] : usersTemplate
 * };
 * ```
 *
 * @param columns An array of localized user template column definitions.
 * @param options An array of other optional column definitions.
 */
export const usersTemplate = ({columns, options = {}}: EntityTemplate) => {
  const columnEntries: TemplateColumn[] = [];
  const columnValidations: TemplateValidation[] = [];
  const templateColumns = {
    [UserBulkEditorFormFields.Status] : statusColumn,
    [UserBulkEditorFormFields.UOM]    : UOMColumn
  };
  columns.forEach((column, index) => {
    const {key, type, required, max, error, width, style} = column;
    const validation = createValidation({required, error, index, type});
    const {header} = column;
    if (error) {
      if (max && required) {
        validation.formulae = [1, max];
        validation.operator = 'between';
      } else if (max) {
        validation.formulae = [max];
        validation.operator = 'lessThanOrEqual';
      }
    }
    columnEntries.push({key, header, width, style});
    columnValidations.push(templateColumns[key]?.({column, validation, options}) || validation);
  });
  return {
    columnEntries,
    columnValidations
  };
};

/**
 * Provides a utility function which manages the generation of spreadsheet errors
 * specific to Users import for download by the user.
 *
 * ```typescript
 *
 * import {getUserImportErrors} from '@ion/core/src/xlsx/UserTemplate';
 *
 * const processedResult = getUserImportErrors(invalidUsers, result);
 * ```
 *
 * @param invalidUsers Users which are invalid to be imported.
 * @param result Response returned from server.
 *
 * @returns The processed results with errors if any
 */
export function getUserImportErrors(
  invalidUsers: CTCEntityResponseWrapper<User>[],
  result: ExtendedResults,
  group: AccountLink
): ExtendedResults {
  if (result?.errors?.length) {
    const formattedErrors = formatErrors(result?.errors, Entity.user);
    result.errors = [];
    result.errors = [...formattedErrors];
  }
  if (invalidUsers?.length) {
    invalidUsers.forEach(({user}: any) => {
      let message;
      const {account}   = user;
      const accountName = group?.title;
      const groupName   = account?.title || (user?.groupName || user?.parentGroup || accountName);
      if (user?.groupName || user?.parentGroup) {
        message = translate('admin-users:import.errors.invalidGroup', {
          user   : user?.username,
          group  : groupName,
          parent : accountName
        });
      } else if (!account) {
        message = translate('admin-users:import.errors.noGroup', {
          user : user?.username
        });
      } else {
        message = translate('admin-users:import.errors.invalidGroupRole', {
          user  : user?.username,
          group : groupName
        });
      }
      result?.errors?.push({
        error : message,
        name  : user?.username,
        message,
        user
      });
    });
  }
  return result;
};

/**
 * Provides a utility function which manages the generation of spreadsheet errors
 * specific to Users import for download by the user.
 *
 * ```typescript
 *
 * import {getUserImportErrors} from '@ion/core/src/xlsx/UserTemplate';
 *
 * const processedResult = getUserImportErrors(invalidUsers, result);
 * ```
 */
export function getBulkUserImportErrors(
  invalidUsers: CTCEntityResponseWrapper<User>[],
  result: ExtendedResults,
  group: AccountLink
): ExtendedResults {
  if (result?.errors?.length) {
    const formattedErrors = formatBulkErrors(result?.errors, Entity.user);
    result.errors = [];
    result.errors = [...formattedErrors];
  }
  if (invalidUsers?.length) {
    invalidUsers.forEach(({user}: any) => {
      let message;
      const {account}   = user;
      const accountName = group?.title;
      const groupName   = account?.title || (user?.groupName || user?.parentGroup || accountName);
      if (user?.groupName || user?.parentGroup) {
        message = translate('admin-users:import.errors.invalidGroup', {
          user   : user?.username,
          group  : groupName,
          parent : accountName
        });
      } else if (!account) {
        message = translate('admin-users:import.errors.noGroup', {
          user : user?.username
        });
      } else {
        message = translate('admin-users:import.errors.invalidGroupRole', {
          user  : user?.username,
          group : groupName
        });
      }
      result?.errors?.push({
        error : message,
        name  : user?.username,
        message,
        user
      });
    });
  }
  return result;
};

/**
 * Provides a utility function which maps the results against the template columns
 * before the same is available as excel for user to download.
 *
 * ```typescript
 *
 * import {exportMapper} from '@ion/core/src/xlsx/UserTemplate';
 *
 * const processedResult = exportMapper(invalidUsers, result);
 * ```
 *
 * @param results Response from server.
 * @param boolean Checks if response has errors only.
 * @param Mapped Map which helps in mapping the results against respective
 * template columns.
 *
 * @returns The results mapped against the respective columns for generating
 * excel data.
 */
export function exportMapper(
  {results, errors}: BulkOperationResults | ExtendedResults,
  errorsOnly: boolean,
  attributes: Mapped
): unknown[] {
  const entries      = [];
  const items        = errorsOnly ? errors : results?.concat(errors);
  const succeeded    = translate('common:succeeded');
  const failed       = translate('common:failed');
  const unavailable  = translate('common:notAvailable');
  items?.forEach(item => {
    const entry         = [];
    const isGlobalError = !isUndefined(item.code);
    const hasErrors     = !isUndefined(item.error) || isGlobalError;
    const result        = hasErrors ? item.user : item;
    Object.values(attributes)?.forEach((attribute: string) => {
      if (!isGlobalError || item.user) {
        const val = result[attribute];
        switch (attribute) {
          case 'groupName':
            entry.push(result.account ? result.account.title : result.groupName);
            break;
          case 'role':
            entry.push(result?.role || (result?.roles?.length ? result?.roles[0]?.title : ''));
            break;
          case 'uom':
            entry.push(result?.unitOfMeasure || result?.uom);
            break;
          default :
            entry.push(val);
            break;
        }
      } else {
        entry.push(unavailable);
      }
    });
    entry.push(hasErrors ? failed : succeeded);
    entry.push(hasErrors ? (!isGlobalError ? item.error : item.message) : '');
    entries.push(entry);
  });
  return entries;
};
